/**
 * JADE TUNNEL — game instance (sc-3443, MVP step 2: sun + jump + pits).
 *
 * Builds on step 1's walking-skeleton:
 *
 *   - **Sun timer.** The sun arcs from sunrise (left horizon, ang=π) to
 *     sunset (right horizon, ang=0) over `SUN_TOTAL_TICKS` (5400 = 90s
 *     @ 60Hz). When it touches the horizon the treasure vault seals and
 *     the run ends. The sun renders in the HUD strip; ground-level
 *     gameplay isn't directly affected by sun position other than the
 *     timer reaching the end.
 *
 *   - **Jump.** fireA when grounded fires a fixed-apex parabolic jump
 *     (`vy = -JUMP_VY`). Gravity adds each tick; player lands when
 *     `playerY + PLAYER_H >= GROUND_Y` AND there's actual ground under
 *     them (no pit). Variable-height (release-to-cancel) deferred to
 *     polish.
 *
 *   - **Bottomless pits.** Each surface screen gets zero-or-one pit at
 *     a per-screen-deterministic x. Pit width is 32-64px. Standing OR
 *     walking into the pit footprint with no ground beneath = fall.
 *
 *   - **Death.** Falling into a pit OR sun-touching-horizon ends the
 *     run. Pit-falls cost a life and respawn at the last surface
 *     screen's safe entrance (left edge). Out-of-lives = game-over.
 *     Sun-out = game-over regardless of lives (treasure vault sealed).
 *
 *   - **Phase machine.** 'playing' | 'falling' | 'respawn' | 'game-over'.
 *     The launcher's isGameOver() / shouldRestart() duck-type hooks
 *     fire on phase transitions.
 *
 * Determinism contract: same `(seed, frames)` → same final state.
 * Pit placement is fully decided in the constructor by consuming
 * `ctx.random` once per surface screen, so the layout is fixed before
 * tick 0. Sun timer is a pure tick counter; jump physics are integer
 * arithmetic on tick increments.
 *
 * Sibling docs: sc-3443, `apps/shell/src/cartridges/cargo-climb/instance.ts`
 * (phase machine + restart pattern reference).
 */

import type {
  GameContext,
  GameInstance,
  InputState,
} from '@arcraz/arcadia-types';

import {
  // sky + jungle
  SKY_TOP,
  SKY_HORIZON,
  SUN_BODY,
  SUN_GLOW,
  CANOPY,
  CANOPY_DARK,
  TRUNK,
  TRUNK_DARK,
  GROUND_TOP,
  GROUND_BOTTOM,
  GROUND_EDGE,
  // hazards
  PIT,
  VINE,
  VINE_HIGHLIGHT,
  VINE_DARK,
  GATOR_BODY,
  GATOR_BACK,
  GATOR_EYE,
  GATOR_TEETH,
  SNAKE_BODY,
  SNAKE_DARK,
  SCORPION_BODY,
  SCORPION_DARK,
  QUICKSAND,
  QUICKSAND_DARK,
  TAR,
  TAR_HIGHLIGHT,
  LOG_BODY,
  LOG_DARK,
  LOG_RING,
  // caves
  CAVE_BG,
  CAVE_WALL,
  CAVE_WALL_DARK,
  CAVE_FLOOR,
  LADDER,
  LADDER_RUNG,
  BAT_BODY,
  BAT_EYE,
  // loot
  IDOL_BODY,
  IDOL_GLOW,
  IDOL_EYE,
  FLASH_PICKUP,
  // player
  PLAYER_BODY,
  PLAYER_OUTLINE,
  PLAYER_HEAD,
  PLAYER_HAT,
  // HUD
  HUD_LABEL,
  HUD_SCORE,
  HUD_LIVES,
  HUD_TIME_OK,
  HUD_TIME_WARN,
  HUD_TIME_CRIT,
  FLASH_HIT,
} from './palette.js';

// ============================================================================
// World constants
// ============================================================================

const WIDTH = 480;
const HEIGHT = 270;

/** Width of one "screen" of the surface in logical pixels. */
const SCREEN_W = 250;

/** Number of surface screens in the MVP track. */
const SCREEN_COUNT = 24;

/** Total horizontal extent of the surface world. */
const WORLD_W = SCREEN_W * SCREEN_COUNT;

/** Ground line — y-coordinate of the top of the ground. */
const GROUND_Y = 200;

/** Player size + walk speed. */
const PLAYER_W = 12;
const PLAYER_H = 24;
const WALK_SPEED = 90; // logical px / sec

/** Jump physics — chosen so apex clears the widest pit (64px) at
 *  walk-speed in roughly 0.7s, matching the Pitfall-style hop. */
const JUMP_VY = 240;     // initial upward velocity (px/s); negative-y on screen
const GRAVITY = 720;     // px/s^2

/** Camera smoothing — fraction of distance closed per tick toward target. */
const CAMERA_LERP = 0.18;

/** Pixels-per-tile for the SCORE readout. */
const SCORE_PX_PER_POINT = 4;

/** Starting lives. */
const START_LIVES = 3;

/** Sun-timer total ticks. 5400 = 90 seconds @ 60Hz. Caves will pause +
 *  add drift penalties in step 5. */
const SUN_TOTAL_TICKS = 5400;

/** Sun-arc geometry — semicircle anchored above the canvas, swung
 *  across the top from left to right. */
const SUN_ARC_CY = 60;
const SUN_ARC_RX = WIDTH / 2 - 30;
const SUN_ARC_RY = 38;
const SUN_R = 8;
const SUN_GLOW_R = 14;

/** Per-screen ground-hazard slot probability: 70% chance a screen has
 *  EITHER a pit or a gator pond (45% pit, 25% gators within that).
 *  Screen 0 always skips so opening read isn't "instantly fall." */
const SCREEN_HAZARD_PROB = 0.7;
const GATOR_VS_PIT_PROB = 0.36; // within hazard slot, fraction that are gators

const PIT_MIN_W = 32;
const PIT_MAX_W = 56;

/** Gator pond geometry. 3 gator-head platforms in a row, equally
 *  spaced inside the pit-footprint. Each head is `GATOR_W` wide. */
const GATOR_POND_W = 80;
const GATOR_W = 18;
const GATOR_H = 14;
/** Synced mouth cycle in ticks. 90 ticks = 1.5s — readable. */
const GATOR_CYCLE = 90;
/** Fraction of cycle that mouths are OPEN (rest = closed). 0.5 = even. */
const GATOR_OPEN_FRAC = 0.45;

/** Vine: pendulum hanging from the canopy. Player jumps into the tip
 *  to grab; second jump releases with the tip's instantaneous velocity. */
const VINE_SPAWN_PROB = 0.4;
/** Vine length range in logical px. Long enough to dip below jump
 *  apex; short enough to swing inside the canvas. */
const VINE_MIN_LEN = 80;
const VINE_MAX_LEN = 110;
/** Vine anchor y (top of vine — pinned to the canopy). */
const VINE_ANCHOR_Y = 56;
/** Vine angular frequency. Sine cycle ~2.6s — slow enough that the
 *  player has time to read the tip's position and jump for it. */
const VINE_OMEGA = (2 * Math.PI) / 156; // radians per tick
/** Max swing angle off vertical. Smaller = easier to predict. */
const VINE_MAX_SWING = Math.PI / 4; // 45 degrees
/** Player grab radius — how close the tip has to be to attach. */
const VINE_GRAB_R = 18;
/** Vine release velocity multiplier — how much horizontal speed the
 *  player carries away from a release. */
const VINE_RELEASE_VX_MULT = 1.2;
const VINE_RELEASE_VY_BOOST = -120; // extra upward kick on release
/** Ticks after a vine release during which re-grab is suppressed. Without
 *  this, releasing with Z immediately re-grabs the same vine on the next
 *  tick because the player is still at the tip's position. */
const VINE_REGRAB_COOLDOWN = 12;

/** Pit variants. When a screen rolls a pit, an inner roll decides
 *  whether it's a plain pit, quicksand, or tar:
 *    - plain pit: bottomless, jump or die on touch
 *    - quicksand: standable but sinks; SINK_GRACE_TICKS to escape via jump
 *    - tar: instant-death on touch (no grace); shiny dark purple
 *  Quicksand + tar both occupy the same ground footprint as a pit. */
const QUICKSAND_PROB = 0.18;  // within pit-spawn pool
const TAR_PROB = 0.12;        // within pit-spawn pool (after quicksand check)
/** Ticks the player can stand on quicksand before sinking. */
const SINK_GRACE_TICKS = 30;  // 0.5s @ 60Hz
/** While sinking, the player slowly descends — this many px per tick. */
const SINK_RATE_PX = 0.4;
/** Once sinkOffset reaches this, the player is GONE. */
const SINK_DEATH_OFFSET = 16;

/** Surface creatures. One of {snake, scorpion, falling-log} may spawn
 *  per screen that DIDN'T spawn a ground hazard (pit / gators) — so
 *  the player isn't fighting two layered hazards at once. */
const CREATURE_SLOT_PROB = 0.55;  // chance the creature slot fills
/** Within the creature slot, weighted choice. The sum is 1.0. */
const SNAKE_WEIGHT = 0.45;
const SCORPION_WEIGHT = 0.30;
// remainder (0.25) = falling log

/** Snake patrol speed (px/sec) + path range relative to spawn-x. */
const SNAKE_SPEED = 35;
const SNAKE_RANGE = 80;
const SNAKE_W = 14;
const SNAKE_H = 5;

const SCORPION_SPEED = 55;
const SCORPION_RANGE = 70;
const SCORPION_W = 10;
const SCORPION_H = 6;

/** Falling log rolls across the WHOLE screen at this speed. Wraps
 *  back to the off-screen left edge on completion. */
const LOG_SPEED = 110;
const LOG_W = 18;
const LOG_H = 10;
/** A log's traversal range starts 30px LEFT of the screen and ends
 *  30px RIGHT of the screen so it rolls fully on/off-screen. */
const LOG_TRAVERSAL_PADDING = 30;

/** Cave portals — per-screen probability that a surface screen has a
 *  cave entrance. Walking over a portal + pressing DOWN enters the cave;
 *  inside, joystick up/down climbs ladders, and reaching the top of the
 *  shaft returns to the surface at the portal. */
const CAVE_PORTAL_PROB = 0.3;
const CAVE_PORTAL_W = 24;
const CAVE_PORTAL_H = 16;
/** Sun-pause: the sun timer does NOT advance while underground. On
 *  emergence, this many extra ticks are added as a "drift" penalty —
 *  caves are a high-reward shortcut but cost a slice of daylight. */
const CAVE_DRIFT_TICKS = 300;

/** Cave geometry. One cave is 480 px wide (same as surface viewport)
 *  and CAVE_H tall. Camera lerps vertically inside the cave when the
 *  player climbs past mid-height. */
const CAVE_W = WIDTH;
const CAVE_H = 540;
/** Ladder x within the cave — single vertical column right of center. */
const LADDER_X = CAVE_W / 2 + 30;
const LADDER_W = 12;
/** Platforms: top (entry/exit), 2 mids, bottom (idol pedestal). The
 *  ladder runs all the way from top platform to bottom platform. */
const CAVE_PLATFORM_H = 8;
const CAVE_PLATFORM_W = 240;

/** Bat — flies in a sine pattern around an anchor; touch = death.
 *  Horizontal range traversal speed is implicit in BAT_OMEGA * BAT_RANGE_X. */
const BAT_W = 12;
const BAT_H = 8;
const BAT_RANGE_X = 80;
const BAT_RANGE_Y = 30;
const BAT_OMEGA = (2 * Math.PI) / 156; // 2.6s flight cycle

/** Climb speed up/down a ladder (px/s). Slower than walk on purpose
 *  — climbing should feel like a commitment. */
const CLIMB_SPEED = 70;

/** Respawn invuln ticks after a fall (player blinks during this). */
const RESPAWN_INVULN_TICKS = 90;

/** Fall animation — once death is triggered, player falls for this
 *  many ticks before respawn/game-over. */
const FALL_TICKS = 36;

/** ----- Loot + bonus scoring (step 6) -----
 *  Each cave hides one jade idol on the bottom-platform pedestal.
 *  Collecting an idol awards a flat bonus and bumps the visible
 *  "idols collected" counter — the cabinet's high-score table cares
 *  about distance score, but idols are the "play it again" hook.
 *
 *  Reaching the rightmost world screen before sundown ends the run as
 *  a WIN and awards FINISHING_BONUS. Surviving to sundown WITHOUT
 *  finishing ends with whatever distance + idols the player had. */
const IDOL_W = 10;
const IDOL_H = 14;
const IDOL_BONUS = 250;       // per idol
const FINISHING_BONUS = 1000; // reach the right edge before sundown
const FINISH_LINE_X = SCREEN_W * SCREEN_COUNT - SCREEN_W / 2;

type Phase = 'playing' | 'falling' | 'respawn' | 'game-over' | 'cave';

interface CavePortal {
  /** World-x of portal center on the surface. */
  readonly x: number;
  /** Which cave room this portal leads to. */
  readonly caveIdx: number;
  /** Surface-screen index. */
  readonly screen: number;
}

interface CavePlatform {
  /** Local cave-x of platform's left edge. */
  readonly x: number;
  /** Local cave-y of platform's top. */
  readonly y: number;
  /** Platform width. */
  readonly w: number;
}

interface Bat {
  /** Local cave-x of flight anchor center. */
  readonly anchorX: number;
  /** Local cave-y of flight anchor center. */
  readonly anchorY: number;
  /** Phase offset in ticks — deterministic per bat. */
  readonly phase: number;
}

interface CaveRoom {
  readonly platforms: readonly CavePlatform[];
  readonly bats: readonly Bat[];
  /** Y of the top platform (= entry/exit point). */
  readonly topY: number;
  /** Y of the bottom platform (= idol pedestal). */
  readonly bottomY: number;
  /** Local cave-x of the idol pedestal center. */
  readonly idolX: number;
  /** Local cave-y of the idol's TOP — sits on bottom platform. */
  readonly idolY: number;
}

type PitKind = 'pit' | 'quicksand' | 'tar';

interface Pit {
  /** World-x of the LEFT edge of the pit. */
  readonly x: number;
  /** Pit width in logical px. */
  readonly w: number;
  /** Surface-screen index this pit is in (used for safe-respawn). */
  readonly screen: number;
  /** Pit variant — drives death rule + visuals. */
  readonly kind: PitKind;
}

type CreatureKind = 'snake' | 'scorpion' | 'log';

interface Creature {
  readonly kind: CreatureKind;
  /** World-x of the spawn anchor (creature oscillates around this). */
  readonly anchorX: number;
  /** Phase offset in ticks — deterministic per creature. */
  readonly phase: number;
  /** Surface-screen index. */
  readonly screen: number;
}

interface GatorPond {
  /** World-x of the LEFT edge of the pond's pit-footprint. */
  readonly x: number;
  /** Pond width — `GATOR_POND_W` always but stored for symmetry. */
  readonly w: number;
  /** Surface-screen index. */
  readonly screen: number;
}

interface Vine {
  /** World-x of the canopy anchor (top of vine). */
  readonly anchorX: number;
  /** Vine length in logical px. */
  readonly length: number;
  /** Phase offset in ticks — deterministic per vine so vines don't all
   *  swing in sync across the level. */
  readonly phase: number;
}

// ============================================================================

export class JadeTunnelInstance implements GameInstance {
  private readonly ctx: GameContext;

  // Player state.
  private playerX = 30;
  private playerY = GROUND_Y - PLAYER_H;
  private vy = 0;
  private grounded = true;
  private facing: 1 | -1 = 1;
  private walkAnimTicks = 0;
  private invulnTicks = 0;
  private fallTicksLeft = 0;

  // Camera + score.
  private cameraX = 0;
  private scorePoints = 0;
  private finalScore = 0;
  private maxX = 30;

  // Sun timer.
  private sunTicks = 0;

  // Lives + phase.
  private lives = START_LIVES;
  private phase: Phase = 'playing';
  private restartArmed = false;

  // Held-input mirror.
  private inLeft = false;
  private inRight = false;
  private inUp = false;
  private inDown = false;
  private jumpJustPressed = false;

  // Track layout — fully determined at construction time so the test
  // suite can verify the same seed produces the same map.
  private readonly pits: readonly Pit[];
  private readonly gatorPonds: readonly GatorPond[];
  private readonly vines: readonly Vine[];
  private readonly creatures: readonly Creature[];
  private readonly portals: readonly CavePortal[];
  private readonly caves: readonly CaveRoom[];

  /** Bitmask-by-index of idols the player has collected this run.
   *  Stays small (one bit per cave portal) so we use a plain Set<number>
   *  rather than fight TypedArrays for ~24 entries. */
  private readonly idolsCollected = new Set<number>();
  /** Cached idol-bonus sum so `getScore()` is O(1) — set when idolsCollected
   *  is mutated. */
  private idolBonus = 0;
  /** Set true when the player crosses the finish line BEFORE sundown.
   *  Awards FINISHING_BONUS and transitions to game-over as a win. */
  private finished = false;

  // Cave state — set when player enters a portal. While inside:
  //   - playerX/playerY are LOCAL CAVE COORDS (NOT world coords)
  //   - cameraX is fixed (cave is 480 wide); cameraY tracks player
  //   - sun timer doesn't tick; on exit, CAVE_DRIFT_TICKS is added
  //   - phase === 'cave'
  private currentCaveIdx = -1;
  /** World-x to return to when exiting the current cave (portal x). */
  private caveExitX = 0;
  /** Vertical camera offset inside the cave. */
  private caveCameraY = 0;
  /** Down-pressed edge — used to enter portals (joystick down at portal
   *  fires the entry transition). */
  private downJustPressed = false;
  /** Up-pressed edge — exits cave when at top platform (mirror of down). */
  private upJustPressed = false;
  /** Whether the player is currently on a ladder (climb mode active). */
  private onLadder = false;

  // Vine-grab state — set when the player attaches to a vine, cleared
  // on release. While grabbed: player position = vine tip; jump fires
  // a release with carry-over velocity instead of a new jump.
  private grabbedVineIdx = -1;
  /** Tick countdown that suppresses vine re-grab right after release. */
  private vineRegrabCooldown = 0;

  /** Quicksand sinking state. When standing on quicksand for > grace,
   *  this counts up; player can escape by jumping (resets to 0). When
   *  it reaches SINK_DEATH_OFFSET, the player drowns. */
  private sinkOffset = 0;
  /** How long the player has been standing on quicksand. Resets on
   *  any move OFF the quicksand or on jump. */
  private quicksandTicks = 0;

  /** Tick counter for vine swing phase. Increments in 'playing' and
   *  'respawn'; pauses during 'falling' so the vine pattern doesn't
   *  drift while the player watches a death animation. */
  private worldTicks = 0;

  constructor(ctx: GameContext) {
    this.ctx = ctx;
    const built = this.buildTrack();
    this.pits = built.pits;
    this.gatorPonds = built.gatorPonds;
    this.vines = built.vines;
    this.creatures = built.creatures;
    this.portals = built.portals;
    // Build the cave rooms AFTER the surface track so PRNG consumption
    // order stays stable. One cave per portal; portals reference caves
    // by index.
    this.caves = this.buildCaves(built.portals.length);
  }

  // --------------------------------------------------------------------------
  // Track construction
  // --------------------------------------------------------------------------

  /** Generate per-screen layout. Each screen consumes EXACTLY 11 PRNG
   *  values whether or not all the slots fire, so tiny seed changes
   *  don't cascade-diverge later screens (important for daily-challenge
   *  fairness). Per-screen slot menu:
   *
   *    rolls[0]  hazardRoll       — fills the ground-hazard slot?
   *    rolls[1]  hazardKindRoll   — pit vs gator-pond
   *    rolls[2]  pitVariantRoll   — plain / quicksand / tar (within pit)
   *    rolls[3]  hazardWidthRoll  — pit width (within size range)
   *    rolls[4]  hazardXRoll      — placement-x within screen
   *    rolls[5]  creatureRoll     — fills the creature slot? (only if no
   *                                 ground hazard)
   *    rolls[6]  creatureKindRoll — snake / scorpion / log
   *    rolls[7]  creaturePhaseRoll
   *    rolls[8]  vineRoll         — vine spawns this screen?
   *    rolls[9]  vineLenAndPhaseRoll
   *    rolls[10] portalRoll       — cave portal spawns this screen?
   */
  private buildTrack(): {
    pits: Pit[];
    gatorPonds: GatorPond[];
    vines: Vine[];
    creatures: Creature[];
    portals: CavePortal[];
  } {
    const pits: Pit[] = [];
    const gatorPonds: GatorPond[] = [];
    const vines: Vine[] = [];
    const creatures: Creature[] = [];
    const portals: CavePortal[] = [];

    for (let s = 1; s < SCREEN_COUNT; s++) {
      const r0 = this.ctx.random.next();
      const r1 = this.ctx.random.next();
      const r2 = this.ctx.random.next();
      const r3 = this.ctx.random.next();
      const r4 = this.ctx.random.next();
      const r5 = this.ctx.random.next();
      const r6 = this.ctx.random.next();
      const r7 = this.ctx.random.next();
      const r8 = this.ctx.random.next();
      const r9 = this.ctx.random.next();
      const r10 = this.ctx.random.next();

      let groundHazardSpawned = false;

      if (r0 <= SCREEN_HAZARD_PROB) {
        groundHazardSpawned = true;
        if (r1 < GATOR_VS_PIT_PROB) {
          const w = GATOR_POND_W;
          const safeLeft = 40;
          const safeRight = SCREEN_W - 40 - w;
          const x =
            s * SCREEN_W +
            safeLeft +
            Math.round(r4 * Math.max(1, safeRight - safeLeft));
          gatorPonds.push({ x, w, screen: s });
        } else {
          // Pit — pit variant chosen from r2.
          let kind: PitKind = 'pit';
          if (r2 < QUICKSAND_PROB) {
            kind = 'quicksand';
          } else if (r2 < QUICKSAND_PROB + TAR_PROB) {
            kind = 'tar';
          }
          const w = Math.round(PIT_MIN_W + r3 * (PIT_MAX_W - PIT_MIN_W));
          const safeLeft = 40;
          const safeRight = SCREEN_W - 40 - w;
          const x =
            s * SCREEN_W +
            safeLeft +
            Math.round(r4 * Math.max(1, safeRight - safeLeft));
          pits.push({ x, w, screen: s, kind });
        }
      }

      // Creature slot — only fills if NO ground hazard (avoid stacking
      // a snake on top of a pit). Slot probability tunable; weighted
      // pick across {snake, scorpion, log}.
      if (!groundHazardSpawned && r5 <= CREATURE_SLOT_PROB) {
        let kind: CreatureKind;
        if (r6 < SNAKE_WEIGHT) {
          kind = 'snake';
        } else if (r6 < SNAKE_WEIGHT + SCORPION_WEIGHT) {
          kind = 'scorpion';
        } else {
          kind = 'log';
        }
        const anchorX = s * SCREEN_W + SCREEN_W / 2;
        const phase = Math.floor(r7 * 256);
        creatures.push({ kind, anchorX, phase, screen: s });
      }

      // Vines — independent of ground / creature slots. Can spawn over
      // a pit/pond (Pitfall-style: vine = pit-traversal aid) OR mid-
      // screen as an optional shortcut over snakes / logs.
      if (r8 <= VINE_SPAWN_PROB) {
        const r9big = r9 * 256;
        const phase = Math.floor(r9big) % 156;
        const lenFrac = r9big - Math.floor(r9big);
        const length = Math.round(VINE_MIN_LEN + lenFrac * (VINE_MAX_LEN - VINE_MIN_LEN));
        const anchorX = s * SCREEN_W + SCREEN_W / 2;
        vines.push({ anchorX, length, phase });
      }

      // Cave portal — independent of all other slots, but skip if the
      // screen already has a ground hazard at the center (we place the
      // portal at the screen's center-x, so a centered pit would block
      // entry). The cave is the high-risk shortcut path; portals get
      // placed on ~30% of screens.
      if (r10 <= CAVE_PORTAL_PROB && !groundHazardSpawned) {
        const portalX = s * SCREEN_W + SCREEN_W / 2;
        portals.push({
          x: portalX,
          caveIdx: portals.length,
          screen: s,
        });
      }
    }

    return { pits, gatorPonds, vines, creatures, portals };
  }

  /** Build one cave room per portal, deterministically from continuing
   *  PRNG draws. Each cave has 2 mid-platforms (above the bottom
   *  pedestal, below the top entry platform) at varying offsets, plus
   *  0-2 bats positioned around the mid-platforms.
   *
   *  Per-cave PRNG schedule (8 rolls):
   *    rolls[0]   midA-x offset
   *    rolls[1]   midA-y offset
   *    rolls[2]   midB-x offset
   *    rolls[3]   midB-y offset
   *    rolls[4]   bat-count discriminator
   *    rolls[5-7] bat-A x/y/phase (always drawn even if no bat — keeps
   *               cave N+1 stable when cave N has 0 bats)
   */
  private buildCaves(count: number): CaveRoom[] {
    const out: CaveRoom[] = [];
    const topY = 30;
    const bottomY = CAVE_H - 30 - CAVE_PLATFORM_H;
    for (let c = 0; c < count; c++) {
      const r0 = this.ctx.random.next();
      const r1 = this.ctx.random.next();
      const r2 = this.ctx.random.next();
      const r3 = this.ctx.random.next();
      const r4 = this.ctx.random.next();
      const r5 = this.ctx.random.next();
      const r6 = this.ctx.random.next();
      const r7 = this.ctx.random.next();
      // Mid platforms are placed at varying y between ~33% and ~66% of
      // the cave height; x sits OFF the ladder column (left half or
      // right of the ladder) so they don't merge with the spine.
      const midA: CavePlatform = {
        x: r0 < 0.5
          ? 30 + Math.round(r0 * 60)
          : LADDER_X + LADDER_W + 30 + Math.round((r0 - 0.5) * 60),
        y: Math.round(topY + CAVE_PLATFORM_H + 60 + r1 * 90),
        w: CAVE_PLATFORM_W * 0.45,
      };
      const midB: CavePlatform = {
        x: r2 < 0.5
          ? 30 + Math.round(r2 * 60)
          : LADDER_X + LADDER_W + 30 + Math.round((r2 - 0.5) * 60),
        y: Math.round(topY + CAVE_PLATFORM_H + 220 + r3 * 90),
        w: CAVE_PLATFORM_W * 0.45,
      };
      // Top + bottom span platforms run full cave-width minus margins.
      const top: CavePlatform = {
        x: (CAVE_W - CAVE_PLATFORM_W) / 2,
        y: topY,
        w: CAVE_PLATFORM_W,
      };
      const bottom: CavePlatform = {
        x: (CAVE_W - CAVE_PLATFORM_W) / 2,
        y: bottomY,
        w: CAVE_PLATFORM_W,
      };
      const platforms: CavePlatform[] = [top, midA, midB, bottom];
      // Bat count: 0 / 1 / 2, weighted ~35% / 45% / 20%.
      const batCount = r4 < 0.35 ? 0 : r4 < 0.8 ? 1 : 2;
      const bats: Bat[] = [];
      // ALWAYS draw the rolls for both potential bats — schedule stability.
      const batRolls = [r5, r6, r7];
      const batRolls2 = [
        this.ctx.random.next(),
        this.ctx.random.next(),
        this.ctx.random.next(),
      ];
      if (batCount >= 1) {
        bats.push({
          anchorX: 80 + batRolls[0]! * (CAVE_W - 160),
          anchorY: midA.y - 40 + batRolls[1]! * 30,
          phase: Math.floor(batRolls[2]! * 156),
        });
      }
      if (batCount >= 2) {
        bats.push({
          anchorX: 80 + batRolls2[0]! * (CAVE_W - 160),
          anchorY: midB.y - 40 + batRolls2[1]! * 30,
          phase: Math.floor(batRolls2[2]! * 156),
        });
      }
      // Idol sits on the bottom platform, centered on the left half
      // (off-ladder side) so it's a clear destination after descending.
      // Identical placement per cave keeps the player's mental model
      // simple: "drop to the bottom, grab the idol, climb back up."
      const idolX = bottom.x + bottom.w * 0.25;
      const idolY = bottom.y - IDOL_H;

      out.push({ platforms, bats, topY, bottomY, idolX, idolY });
    }
    return out;
  }

  // --------------------------------------------------------------------------
  // Vine kinematics
  // --------------------------------------------------------------------------

  /** Position of a vine's tip at the current tick. */
  private vineTip(v: Vine): { x: number; y: number; vx: number } {
    const t = this.worldTicks + v.phase;
    const ang = Math.sin(t * VINE_OMEGA) * VINE_MAX_SWING;
    const x = v.anchorX + Math.sin(ang) * v.length;
    const y = VINE_ANCHOR_Y + Math.cos(ang) * v.length;
    // Tangential velocity at the tip (derivative of x w.r.t. tick * 60Hz).
    // For release-velocity carry-over.
    const angRate = Math.cos(t * VINE_OMEGA) * VINE_OMEGA * VINE_MAX_SWING;
    const vx = Math.cos(ang) * v.length * angRate * 60; // px/s
    return { x, y, vx };
  }

  // --------------------------------------------------------------------------
  // GameInstance contract
  // --------------------------------------------------------------------------

  input(state: InputState): void {
    if (this.phase === 'game-over') {
      if (state.justPressed.has('fireA') || state.justPressed.has('start')) {
        this.restartArmed = true;
      }
      return;
    }
    this.inLeft = state.left;
    this.inRight = state.right;
    this.inUp = state.up;
    this.inDown = state.down;
    this.jumpJustPressed =
      state.justPressed.has('fireA') || state.justPressed.has('start');
    this.downJustPressed = state.justPressed.has('down');
    this.upJustPressed = state.justPressed.has('up');
  }

  update(_dt: number): void {
    // Fixed-step contract: ignore _dt and treat one tick as 1/60s.
    const dt = 1 / 60;

    if (this.phase === 'game-over') return;

    // ---- Cave branch — fully separate update path ----
    // Inside the cave: sun timer is paused, surface entities don't tick,
    // player navigates a 480x540 cavern with ladders + bats. Exit costs
    // CAVE_DRIFT_TICKS of sun on emerge (charged in exitCave).
    if (this.phase === 'cave') {
      this.updateCave(dt);
      return;
    }

    // ---- World tick (vine swing, gator cycle) ----
    // Advances during 'playing' + 'respawn'. Pauses during 'falling' so
    // the vine pattern doesn't drift while the player watches a death
    // animation. The world ticks BEFORE input/physics so the vine tip
    // the player is grabbing this tick reflects the latest swing.
    if (this.phase === 'playing' || this.phase === 'respawn') {
      this.worldTicks += 1;
    }

    // ---- Sun timer ----
    // Ticks ONLY during 'playing' so death animations don't burn the
    // clock. (Caves in step 5 will pause this differently.)
    if (this.phase === 'playing') {
      this.sunTicks += 1;
      if (this.sunTicks >= SUN_TOTAL_TICKS) {
        this.enterGameOver();
        return;
      }
    }

    // ---- Falling state — animate the fall, then respawn or end ----
    if (this.phase === 'falling') {
      this.fallTicksLeft -= 1;
      this.vy += GRAVITY * dt;
      this.playerY += this.vy * dt;
      if (this.fallTicksLeft <= 0) {
        if (this.lives > 0) {
          this.lives -= 1;
          this.respawnAtLastSafe();
        } else {
          this.enterGameOver();
        }
      }
      return;
    }

    // ---- Respawn invuln countdown ----
    if (this.phase === 'respawn') {
      this.invulnTicks -= 1;
      if (this.invulnTicks <= 0) {
        this.phase = 'playing';
      }
    }

    // ---- Vine-grabbed branch ----
    // While grabbed, player position is locked to the vine's tip. The
    // second jump-press releases with a release-velocity carry-over
    // so the player swings off the vine in the direction it was moving.
    if (this.grabbedVineIdx >= 0) {
      const v = this.vines[this.grabbedVineIdx]!;
      const tip = this.vineTip(v);
      this.playerX = tip.x - PLAYER_W / 2;
      this.playerY = tip.y - PLAYER_H / 2;
      if (this.jumpJustPressed) {
        // Release — carry the tip's horizontal velocity + a small
        // upward kick so the release feels like a launch, not a drop.
        this.vy = VINE_RELEASE_VY_BOOST;
        // Stash the carry-over vx by translating it into a momentum
        // override — we don't have an X-velocity field today, so apply
        // it implicitly via facing + a single post-release "boost
        // tick" handled below. Simpler: just unanchor and let gravity
        // take over; tip velocity already carried the player along.
        // (Polish step can add lateral momentum carryover.)
        this.grabbedVineIdx = -1;
        this.grounded = false;
        this.facing = tip.vx >= 0 ? 1 : -1;
        // Apply a small kick in the release direction for arcade feel.
        this.playerX += tip.vx * VINE_RELEASE_VX_MULT * dt;
        // Suppress immediate re-grab — player is still standing in the
        // tip's hit-radius for a few ticks after release.
        this.vineRegrabCooldown = VINE_REGRAB_COOLDOWN;
        this.ctx.audio.sfx('jump');
      }
      this.jumpJustPressed = false;
      // Camera follows vine-tip player position too.
      const camX = this.playerX + PLAYER_W / 2 - WIDTH / 2;
      this.cameraX += (camX - this.cameraX) * CAMERA_LERP;
      if (this.cameraX < 0) this.cameraX = 0;
      if (this.cameraX > WORLD_W - WIDTH) {
        this.cameraX = WORLD_W - WIDTH;
      }
      return;
    }

    // ---- Walk physics ----
    let vx = 0;
    if (this.inLeft && !this.inRight) {
      vx = -WALK_SPEED;
      this.facing = -1;
    } else if (this.inRight && !this.inLeft) {
      vx = WALK_SPEED;
      this.facing = 1;
    }
    this.playerX += vx * dt;

    if (this.playerX < 0) this.playerX = 0;
    if (this.playerX > WORLD_W - PLAYER_W) {
      this.playerX = WORLD_W - PLAYER_W;
    }

    if (vx !== 0) {
      this.walkAnimTicks += 1;
    } else {
      this.walkAnimTicks = 0;
    }

    // ---- Jump ----
    if (this.jumpJustPressed && this.grounded) {
      this.vy = -JUMP_VY;
      this.grounded = false;
      // Reset quicksand sink — jumping is the escape verb.
      this.quicksandTicks = 0;
      this.sinkOffset = 0;
      this.ctx.audio.sfx('jump');
    }
    this.jumpJustPressed = false;

    // ---- Vertical physics ----
    if (!this.grounded) {
      this.vy += GRAVITY * dt;
      this.playerY += this.vy * dt;

      // Vine grab while airborne. Generous enough to catch falling-past
      // vines — matches Pitfall II's catch feel. Skipped during the
      // post-release cooldown so the released vine doesn't re-catch.
      if (this.vineRegrabCooldown > 0) {
        this.vineRegrabCooldown -= 1;
      } else {
        this.tryGrabVine();
        if (this.grabbedVineIdx >= 0) return;
      }

      // Land on ground — but only if there's standable ground under the
      // player at the landing x.
      if (this.playerY + PLAYER_H >= GROUND_Y) {
        const hazard = this.standingHazard();
        if (hazard === 'pit' || hazard === 'gator-bite') {
          this.beginFall(hazard);
          return;
        }
        if (hazard === 'tar') {
          // Tar = instant death on touch (no grace). Distinct from a
          // generic pit-fall: vine is the only safe crossing.
          this.beginFall('tar');
          return;
        }
        if (hazard === 'quicksand') {
          // Touchable but not safe. Land, then sinking starts on the
          // following grounded-tick path below.
          this.playerY = GROUND_Y - PLAYER_H + this.sinkOffset;
          this.vy = 0;
          this.grounded = true;
          this.ctx.audio.sfx('land');
        } else {
          // Plain standable ground.
          this.playerY = GROUND_Y - PLAYER_H;
          this.vy = 0;
          this.grounded = true;
          this.ctx.audio.sfx('land');
          this.quicksandTicks = 0;
          this.sinkOffset = 0;
        }
      }
    } else {
      // Grounded — check what we're standing on.
      const hazard = this.standingHazard();
      if (hazard === 'pit' || hazard === 'gator-bite' || hazard === 'tar') {
        this.beginFall(hazard === 'gator-bite' ? 'gator-bite' : 'pit');
        return;
      }
      if (hazard === 'quicksand') {
        // Standing on quicksand. After SINK_GRACE_TICKS, the player
        // starts to slowly descend; if sinkOffset reaches the death
        // threshold without jumping, drowning death.
        this.quicksandTicks += 1;
        if (this.quicksandTicks > SINK_GRACE_TICKS) {
          this.sinkOffset += SINK_RATE_PX;
          this.playerY = GROUND_Y - PLAYER_H + this.sinkOffset;
          if (this.sinkOffset >= SINK_DEATH_OFFSET) {
            this.beginFall('quicksand');
            return;
          }
        }
      } else {
        // Walked off quicksand — reset sink state.
        this.quicksandTicks = 0;
        this.sinkOffset = 0;
      }
    }

    // ---- Creature collision (snake / scorpion / log) ----
    // Skip during respawn invuln — gives the player a moment to escape
    // a creature that respawned them onto its path.
    if (this.phase === 'playing' && this.creatureHit()) {
      this.beginFall('creature');
      return;
    }

    // ---- Cave portal entry ----
    // Press DOWN while standing over a portal to descend into the cave.
    // Pauses sun timer; player position is saved so we can return to it.
    if (
      this.phase === 'playing' &&
      this.grounded &&
      this.downJustPressed
    ) {
      const portal = this.portalAtPlayer();
      if (portal) {
        this.enterCave(portal);
        this.downJustPressed = false;
        return;
      }
    }
    this.downJustPressed = false;

    // ---- Score ----
    if (this.playerX > this.maxX) {
      this.maxX = this.playerX;
      this.scorePoints = Math.floor(this.maxX / SCORE_PX_PER_POINT);
    }

    // ---- Finish line ----
    // Crossing FINISH_LINE_X before sundown awards FINISHING_BONUS and
    // ends the run as a WIN. The "win" path uses the same game-over
    // phase but renders a different headline (see renderGameOver).
    if (
      this.phase === 'playing' &&
      !this.finished &&
      this.playerX + PLAYER_W / 2 >= FINISH_LINE_X
    ) {
      this.finished = true;
      this.enterGameOver();
      return;
    }

    // ---- Camera ----
    const targetCamX = this.playerX + PLAYER_W / 2 - WIDTH / 2;
    this.cameraX += (targetCamX - this.cameraX) * CAMERA_LERP;
    if (this.cameraX < 0) this.cameraX = 0;
    if (this.cameraX > WORLD_W - WIDTH) {
      this.cameraX = WORLD_W - WIDTH;
    }
  }

  render(g: CanvasRenderingContext2D): void {
    if (this.phase === 'cave') {
      this.renderCave(g);
      this.renderHud(g);
      return;
    }

    // 1. Sky gradient.
    const sky = g.createLinearGradient(0, 0, 0, GROUND_Y);
    sky.addColorStop(0, SKY_TOP);
    sky.addColorStop(1, SKY_HORIZON);
    g.fillStyle = sky;
    g.fillRect(0, 0, WIDTH, GROUND_Y);

    // 2. Sun — arcing across the top.
    this.renderSun(g);

    // 3. Canopy strip (parallax).
    this.renderCanopy(g);

    // 4. Tree-trunk silhouettes (parallax).
    this.renderTrunks(g);

    // 5. Ground + pits + gator-pond footprints (carved into ground).
    this.renderGround(g);

    // 6. Cave portals — archway carved into the ground.
    this.renderPortals(g);

    // 7. Gators — drawn ABOVE the ground carve-out so the heads sit on
    // top of their pit slots.
    this.renderGators(g);

    // 8. Surface creatures — snakes, scorpions, rolling logs. Drawn
    // after ground so they sit on the ground line; before vines so a
    // vine swung overhead doesn't get hidden by a creature beneath.
    this.renderCreatures(g);

    // 9. Vines — drawn after creatures so they hang in front of the
    // ground line, but before the player so the player appears ON the
    // vine when grabbed.
    this.renderVines(g);

    // 10. Player.
    this.renderPlayer(g);

    // 11. HUD.
    this.renderHud(g);

    // 12. Game-over overlay.
    if (this.phase === 'game-over') {
      this.renderGameOver(g);
    }
  }

  resize(_w: number, _h: number): void {
    // Logical resolution fixed; compositor handles display scaling.
  }

  pause(): void {
    // No long-running audio loops yet.
  }

  resume(): void {
    // No-op while audio is silent on hold.
  }

  teardown(): void {
    // No external resources held.
  }

  getScore(): number {
    return this.phase === 'game-over'
      ? this.finalScore
      : this.scorePoints + this.idolBonus;
  }

  getFinalScore(): number {
    return this.phase === 'game-over'
      ? this.finalScore
      : this.scorePoints + this.idolBonus;
  }

  isGameOver(): boolean {
    return this.phase === 'game-over';
  }

  shouldRestart(): boolean {
    return this.phase === 'game-over' && this.restartArmed;
  }

  // --------------------------------------------------------------------------
  // Mechanics helpers
  // --------------------------------------------------------------------------

  /** Classify the ground hazard the player's footprint is currently over.
   *  `null` = standable ground / no hazard. `'quicksand'` is a special
   *  case — standable for SINK_GRACE_TICKS, then lethal. Center-column
   *  test avoids the "barely touched the edge" feel; players who jump
   *  over a lip should clear. */
  private standingHazard():
    | 'pit'
    | 'tar'
    | 'quicksand'
    | 'gator-bite'
    | null {
    const playerFootX = this.playerX + PLAYER_W / 2;
    for (const p of this.pits) {
      if (playerFootX > p.x + 2 && playerFootX < p.x + p.w - 2) {
        return p.kind;
      }
    }
    for (const pond of this.gatorPonds) {
      if (playerFootX > pond.x && playerFootX < pond.x + pond.w) {
        const slotW = pond.w / 3;
        const slot = Math.floor((playerFootX - pond.x) / slotW);
        const gatorCenterX = pond.x + slot * slotW + slotW / 2;
        const dx = Math.abs(playerFootX - gatorCenterX);
        if (dx > GATOR_W / 2) {
          return 'pit';
        }
        if (this.gatorMouthOpen(slot)) {
          return 'gator-bite';
        }
        return null;
      }
    }
    return null;
  }

  // --------------------------------------------------------------------------
  // Surface creatures — patrol kinematics + collision
  // --------------------------------------------------------------------------

  /** Current world-x of a creature. Snakes + scorpions oscillate around
   *  their anchorX with sinusoidal motion. Logs roll linearly across a
   *  fixed traversal range and wrap. */
  private creaturePos(c: Creature): number {
    const t = this.worldTicks + c.phase;
    switch (c.kind) {
      case 'snake': {
        const dt = t / 60;
        const omega = (SNAKE_SPEED / SNAKE_RANGE);
        return c.anchorX + Math.sin(dt * omega) * SNAKE_RANGE;
      }
      case 'scorpion': {
        const dt = t / 60;
        const omega = (SCORPION_SPEED / SCORPION_RANGE);
        return c.anchorX + Math.sin(dt * omega) * SCORPION_RANGE;
      }
      case 'log': {
        // Traversal: anchorX is screen center; range = screen width +
        // padding either side. Roll continuously L→R, wrap.
        const traversalW = SCREEN_W + LOG_TRAVERSAL_PADDING * 2;
        const left = c.anchorX - traversalW / 2;
        const dt = t / 60;
        const xInRange = (dt * LOG_SPEED) % traversalW;
        return left + xInRange;
      }
    }
  }

  /** Bounding box for a creature at the current tick. Center-X is from
   *  creaturePos; box dimensions are kind-specific. */
  private creatureBox(c: Creature): {
    x: number;
    y: number;
    w: number;
    h: number;
  } {
    const cx = this.creaturePos(c);
    switch (c.kind) {
      case 'snake':
        return {
          x: cx - SNAKE_W / 2,
          y: GROUND_Y - SNAKE_H,
          w: SNAKE_W,
          h: SNAKE_H,
        };
      case 'scorpion':
        return {
          x: cx - SCORPION_W / 2,
          y: GROUND_Y - SCORPION_H,
          w: SCORPION_W,
          h: SCORPION_H,
        };
      case 'log':
        return {
          x: cx - LOG_W / 2,
          y: GROUND_Y - LOG_H,
          w: LOG_W,
          h: LOG_H,
        };
    }
  }

  /** Whether the player AABB overlaps any creature's box. The check is
   *  generous on the y-axis (creatures are short, on the ground) so
   *  "jump over by a pixel" still counts as cleared. */
  private creatureHit(): boolean {
    const px = this.playerX;
    const py = this.playerY;
    const pw = PLAYER_W;
    const ph = PLAYER_H;
    for (const c of this.creatures) {
      // Cull creatures whose screen is far from the player.
      const dxScreens = Math.abs(c.screen - Math.floor(px / SCREEN_W));
      if (dxScreens > 1) continue;
      const b = this.creatureBox(c);
      if (px + pw <= b.x) continue;
      if (px >= b.x + b.w) continue;
      if (py + ph <= b.y) continue;
      if (py >= b.y + b.h) continue;
      return true;
    }
    return false;
  }

  /** Whether a gator's mouth is currently open. All 3 in a pond share
   *  the same cycle. Slot 0/1/2 each get a small phase offset so
   *  scoring "wait then run" is harder than just "all open / all closed". */
  private gatorMouthOpen(slot: number): boolean {
    const slotPhaseOffset = slot * 18; // ~0.3s stagger per head
    const t = (this.worldTicks + slotPhaseOffset) % GATOR_CYCLE;
    return t < GATOR_CYCLE * GATOR_OPEN_FRAC;
  }

  /** Check whether the player's current position is close enough to any
   *  vine's current tip to attach. Sets `grabbedVineIdx` if so. Only the
   *  closest tip wins on overlapping vines. */
  private tryGrabVine(): void {
    const playerCx = this.playerX + PLAYER_W / 2;
    const playerCy = this.playerY + PLAYER_H / 2;
    let bestIdx = -1;
    let bestDist = VINE_GRAB_R * VINE_GRAB_R;
    for (let i = 0; i < this.vines.length; i++) {
      const v = this.vines[i]!;
      // Cull vines way off-screen to save work.
      if (Math.abs(v.anchorX - playerCx) > VINE_MAX_LEN + 80) continue;
      const tip = this.vineTip(v);
      const dx = tip.x - playerCx;
      const dy = tip.y - playerCy;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestDist) {
        bestDist = d2;
        bestIdx = i;
      }
    }
    if (bestIdx >= 0) {
      this.grabbedVineIdx = bestIdx;
      this.vy = 0;
      this.ctx.audio.sfx('vine-grab');
    }
  }

  private beginFall(
    cause:
      | 'pit'
      | 'gator-bite'
      | 'tar'
      | 'quicksand'
      | 'creature' = 'pit'
  ): void {
    this.phase = 'falling';
    this.fallTicksLeft = FALL_TICKS;
    this.grounded = false;
    this.vy = 60; // initial downward speed; gravity adds to it
    // Audio routing per cause — distinct SFX so the player learns what
    // got them. Tar + quicksand fall through to generic death noise
    // (they're variants of "you sank"); gator gets its chomp; creature
    // gets its own bite/crunch noise registered below in index.ts.
    if (cause === 'gator-bite') {
      this.ctx.audio.sfx('chomp');
    } else if (cause === 'creature') {
      this.ctx.audio.sfx('bite');
    } else {
      this.ctx.audio.sfx('death');
    }
  }

  /** Respawn at the start of the surface screen the player was last on
   *  (NOT the pit screen — that would loop the player into the same
   *  pit). Always respawn at the LEFT edge of the previous screen.
   *  Falling backward from screen 0 is impossible because there's no
   *  pit on screen 0. */
  private respawnAtLastSafe(): void {
    const lastSafeScreen = Math.max(
      0,
      Math.floor(this.playerX / SCREEN_W) - 1
    );
    this.playerX = lastSafeScreen * SCREEN_W + 20;
    this.playerY = GROUND_Y - PLAYER_H;
    this.vy = 0;
    this.grounded = true;
    this.grabbedVineIdx = -1;
    this.invulnTicks = RESPAWN_INVULN_TICKS;
    this.phase = 'respawn';
    // Don't rewind score / maxX — backtracking after a fall isn't
    // rewarded but doesn't lose existing distance.
    // Don't rewind worldTicks — vine swing + gator cycle keep their
    // rhythm so the scene "lives on" past respawn.
  }

  private enterGameOver(): void {
    this.phase = 'game-over';
    // Final score = distance points + idol bonuses + (finish bonus if WIN).
    this.finalScore =
      this.scorePoints + this.idolBonus + (this.finished ? FINISHING_BONUS : 0);
  }

  // --------------------------------------------------------------------------
  // Caves — entry / exit / update / collision
  // --------------------------------------------------------------------------

  /** Portal whose footprint the player is currently over, or null. The
   *  portal sits at a screen's center-x on the ground; the door is
   *  `CAVE_PORTAL_W` wide. */
  private portalAtPlayer(): CavePortal | null {
    const footX = this.playerX + PLAYER_W / 2;
    for (const p of this.portals) {
      if (footX > p.x - CAVE_PORTAL_W / 2 && footX < p.x + CAVE_PORTAL_W / 2) {
        return p;
      }
    }
    return null;
  }

  /** Transition surface → cave. Saves the surface playerX as caveExitX,
   *  switches phase, and drops the player onto the cave's top platform. */
  private enterCave(portal: CavePortal): void {
    this.caveExitX = portal.x;
    this.currentCaveIdx = portal.caveIdx;
    this.phase = 'cave';
    // Drop in on the top platform, just left of the ladder so DOWN
    // immediately snaps to the ladder for the descent.
    const cave = this.caves[portal.caveIdx]!;
    const top = cave.platforms[0]!;
    this.playerX = LADDER_X + LADDER_W / 2 - PLAYER_W / 2;
    this.playerY = top.y - PLAYER_H;
    this.vy = 0;
    this.grounded = true;
    this.onLadder = false;
    this.grabbedVineIdx = -1;
    this.caveCameraY = 0;
    this.ctx.audio.sfx('vine-grab');
  }

  /** Transition cave → surface. Applies CAVE_DRIFT_TICKS sun penalty
   *  (the cost of the cave shortcut) and restores surface playerX. */
  private exitCave(): void {
    this.sunTicks = Math.min(SUN_TOTAL_TICKS, this.sunTicks + CAVE_DRIFT_TICKS);
    this.playerX = this.caveExitX - PLAYER_W / 2;
    this.playerY = GROUND_Y - PLAYER_H;
    this.vy = 0;
    this.grounded = true;
    this.onLadder = false;
    this.currentCaveIdx = -1;
    this.phase = 'playing';
    this.ctx.audio.sfx('land');
  }

  /** Current world-x/y of a bat. Bats fly in a horizontal sinusoid
   *  around `anchorX` with a smaller vertical bob, both phased by the
   *  per-bat phase. Same deterministic kinematic as surface creatures. */
  private batPos(b: Bat): { x: number; y: number } {
    const t = this.worldTicks + b.phase;
    const x = b.anchorX + Math.sin(t * BAT_OMEGA) * BAT_RANGE_X;
    const y =
      b.anchorY + Math.sin(t * BAT_OMEGA * 2) * BAT_RANGE_Y;
    return { x, y };
  }

  /** Pick up the current cave's idol if the player overlaps it and
   *  hasn't already collected it. Awards IDOL_BONUS, updates the
   *  cached bonus sum, and plays a pickup chime. */
  private tryCollectIdol(): void {
    if (this.currentCaveIdx < 0) return;
    if (this.idolsCollected.has(this.currentCaveIdx)) return;
    const cave = this.caves[this.currentCaveIdx]!;
    const ix = cave.idolX - IDOL_W / 2;
    const iy = cave.idolY;
    if (this.playerX + PLAYER_W <= ix) return;
    if (this.playerX >= ix + IDOL_W) return;
    if (this.playerY + PLAYER_H <= iy) return;
    if (this.playerY >= iy + IDOL_H) return;
    this.idolsCollected.add(this.currentCaveIdx);
    this.idolBonus += IDOL_BONUS;
    this.ctx.audio.sfx('idol-pickup');
  }

  /** AABB-test player against all bats in the current cave. */
  private caveBatHit(): boolean {
    if (this.currentCaveIdx < 0) return false;
    const cave = this.caves[this.currentCaveIdx]!;
    const px = this.playerX;
    const py = this.playerY;
    for (const b of cave.bats) {
      const p = this.batPos(b);
      const bx = p.x - BAT_W / 2;
      const by = p.y - BAT_H / 2;
      if (px + PLAYER_W <= bx) continue;
      if (px >= bx + BAT_W) continue;
      if (py + PLAYER_H <= by) continue;
      if (py >= by + BAT_H) continue;
      return true;
    }
    return false;
  }

  /** Find the cave platform whose top the player would land on at the
   *  current x, or null if the player is between platforms. */
  private caveStandingPlatform(): CavePlatform | null {
    if (this.currentCaveIdx < 0) return null;
    const cave = this.caves[this.currentCaveIdx]!;
    const footX = this.playerX + PLAYER_W / 2;
    const footY = this.playerY + PLAYER_H;
    let best: CavePlatform | null = null;
    let bestY = Infinity;
    for (const plat of cave.platforms) {
      if (footX < plat.x || footX > plat.x + plat.w) continue;
      // Only consider platforms at or below the player's foot (we're
      // falling onto it, not phasing through it).
      if (plat.y < footY - 2) continue;
      if (plat.y < bestY) {
        bestY = plat.y;
        best = plat;
      }
    }
    return best;
  }

  /** Whether the player's center is over the ladder column. */
  private onLadderColumn(): boolean {
    const cx = this.playerX + PLAYER_W / 2;
    return cx > LADDER_X && cx < LADDER_X + LADDER_W;
  }

  private updateCave(dt: number): void {
    // World tick keeps advancing so bat motion lives on a stable phase
    // schedule shared with the surface. Sun is the only thing paused.
    this.worldTicks += 1;

    // Bat collision — instant death (consumes a life). Same beginFall
    // flow as surface hazards.
    if (this.caveBatHit()) {
      this.beginFallInCave();
      return;
    }

    // Idol pickup — AABB-overlap with the cave's idol. Stays collected
    // for the rest of the run (idolsCollected is the source of truth).
    this.tryCollectIdol();

    // Walk physics.
    let vx = 0;
    if (this.inLeft && !this.inRight) {
      vx = -WALK_SPEED;
      this.facing = -1;
    } else if (this.inRight && !this.inLeft) {
      vx = WALK_SPEED;
      this.facing = 1;
    }
    this.playerX += vx * dt;
    if (this.playerX < 0) this.playerX = 0;
    if (this.playerX > CAVE_W - PLAYER_W) this.playerX = CAVE_W - PLAYER_W;
    if (vx !== 0) {
      this.walkAnimTicks += 1;
    } else {
      this.walkAnimTicks = 0;
    }

    // Ladder mode — when the player's column overlaps the ladder AND
    // they hold up/down, they snap to the ladder and climb. Releases
    // when they step sideways off the column.
    const overLadder = this.onLadderColumn();
    if (overLadder && (this.inUp || this.inDown)) {
      this.onLadder = true;
    }
    if (!overLadder) {
      this.onLadder = false;
    }

    if (this.onLadder) {
      // Lock x to ladder center; climb up/down. No gravity while climbing.
      this.playerX = LADDER_X + LADDER_W / 2 - PLAYER_W / 2;
      this.vy = 0;
      this.grounded = false;
      if (this.inUp && !this.inDown) {
        this.playerY -= CLIMB_SPEED * dt;
      } else if (this.inDown && !this.inUp) {
        this.playerY += CLIMB_SPEED * dt;
      }
      const cave = this.caves[this.currentCaveIdx]!;
      const minY = cave.topY - PLAYER_H + CAVE_PLATFORM_H;
      const maxY = cave.bottomY - PLAYER_H;
      if (this.playerY < minY) this.playerY = minY;
      if (this.playerY > maxY) this.playerY = maxY;
    } else {
      // Free-fall in cave — gravity pulls the player to the next
      // platform under them. Jump is allowed off any platform.
      if (this.jumpJustPressed && this.grounded) {
        this.vy = -JUMP_VY;
        this.grounded = false;
        this.ctx.audio.sfx('jump');
      }
      if (!this.grounded) {
        this.vy += GRAVITY * dt;
        this.playerY += this.vy * dt;
        const plat = this.caveStandingPlatform();
        if (plat && this.playerY + PLAYER_H >= plat.y && this.vy >= 0) {
          this.playerY = plat.y - PLAYER_H;
          this.vy = 0;
          this.grounded = true;
          this.ctx.audio.sfx('land');
        }
      } else {
        // Make sure we still have a platform under us — walking off an
        // edge transitions back to airborne.
        const plat = this.caveStandingPlatform();
        if (!plat) {
          this.grounded = false;
        } else if (plat.y - PLAYER_H !== Math.round(this.playerY)) {
          // Snap to platform top (handles tiny float drift after walking
          // across the seam between two platforms at the same y).
          this.playerY = plat.y - PLAYER_H;
        }
      }
    }
    this.jumpJustPressed = false;

    // Exit — pressing UP while on the top platform exits the cave. The
    // top platform's y matches cave.topY, so when player foot Y is
    // within a pixel of topY and they press up, we surface.
    const cave = this.caves[this.currentCaveIdx]!;
    const onTopPlatform =
      this.grounded &&
      Math.abs(this.playerY + PLAYER_H - cave.topY) < 2;
    if (onTopPlatform && this.upJustPressed) {
      this.exitCave();
      this.upJustPressed = false;
      this.downJustPressed = false;
      return;
    }
    this.upJustPressed = false;
    this.downJustPressed = false;

    // Camera follows player vertically — keeps the player roughly
    // centered in the visible 270-tall window.
    const targetCamY = this.playerY + PLAYER_H / 2 - HEIGHT / 2;
    this.caveCameraY += (targetCamY - this.caveCameraY) * CAMERA_LERP;
    if (this.caveCameraY < 0) this.caveCameraY = 0;
    if (this.caveCameraY > CAVE_H - HEIGHT) {
      this.caveCameraY = CAVE_H - HEIGHT;
    }
  }

  /** Bat-induced death. Like beginFall but the respawn lands the player
   *  back on the surface (cave is a single failed attempt — no looping). */
  private beginFallInCave(): void {
    this.phase = 'falling';
    this.fallTicksLeft = FALL_TICKS;
    this.grounded = false;
    this.vy = 60;
    this.ctx.audio.sfx('bite');
    // Move the player back to the surface position so the fall
    // animation + respawn use the same coord space as a surface death.
    // The fall animation will play out at the surface portal; respawn
    // logic in respawnAtLastSafe uses world coords, which we restore here.
    this.playerX = this.caveExitX - PLAYER_W / 2;
    this.playerY = GROUND_Y - PLAYER_H;
    this.currentCaveIdx = -1;
    this.onLadder = false;
    // Charge the drift penalty even on cave-death — fair, since the
    // player went underground.
    this.sunTicks = Math.min(SUN_TOTAL_TICKS, this.sunTicks + CAVE_DRIFT_TICKS);
  }

  // --------------------------------------------------------------------------
  // Rendering helpers
  // --------------------------------------------------------------------------

  private renderSun(g: CanvasRenderingContext2D): void {
    // Sun travels along a top-of-canvas arc: sunrise (ang=π, left
    // horizon) → noon (ang=π/2, top of arc) → sunset (ang=0, right
    // horizon). The arc's center is below the canvas (at SUN_ARC_CY +
    // SUN_ARC_RY) so the visible portion is roughly the upper half of
    // an ellipse — a sun-track skimming above the ground line.
    const progress = Math.min(this.sunTicks / SUN_TOTAL_TICKS, 1);
    const ang = Math.PI * (1 - progress);
    const cx = WIDTH / 2 + Math.cos(ang) * SUN_ARC_RX;
    const cy = SUN_ARC_CY - Math.sin(ang) * SUN_ARC_RY;
    g.fillStyle = SUN_GLOW;
    g.beginPath();
    g.arc(cx, cy, SUN_GLOW_R, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = SUN_BODY;
    g.beginPath();
    g.arc(cx, cy, SUN_R, 0, Math.PI * 2);
    g.fill();

    // Faint horizon line where the sun is heading — gives the player a
    // visual cue that "the sun will set THERE."
    g.fillStyle = 'rgba(255, 48, 136, 0.18)';
    g.fillRect(WIDTH / 2 + SUN_ARC_RX - 1, SUN_ARC_CY - 1, 3, 3);
  }

  private renderCanopy(g: CanvasRenderingContext2D): void {
    const canopyParallax = 0.4;
    const offsetX = -this.cameraX * canopyParallax;
    g.fillStyle = CANOPY_DARK;
    for (let i = 0; i < 60; i++) {
      const cx = (i * 110 + 40) % (WORLD_W * canopyParallax) + offsetX;
      if (cx < -80 || cx > WIDTH + 80) continue;
      const cy = 30 + ((i * 13) % 18);
      g.beginPath();
      g.ellipse(cx, cy, 80, 24, 0, 0, Math.PI * 2);
      g.fill();
    }
    g.fillStyle = CANOPY;
    for (let i = 0; i < 60; i++) {
      const cx = (i * 90 + 70) % (WORLD_W * canopyParallax) + offsetX;
      if (cx < -70 || cx > WIDTH + 70) continue;
      const cy = 22 + ((i * 17) % 16);
      g.beginPath();
      g.ellipse(cx, cy, 70, 18, 0, 0, Math.PI * 2);
      g.fill();
    }
  }

  private renderTrunks(g: CanvasRenderingContext2D): void {
    const trunkParallax = 0.7;
    const offsetX = -this.cameraX * trunkParallax;
    const stride = 95;
    g.fillStyle = TRUNK_DARK;
    for (let i = 0; i < 100; i++) {
      const wx = i * stride + 18;
      const sx = wx * trunkParallax + offsetX;
      if (sx < -8 || sx > WIDTH + 8) continue;
      const h = 80 + ((i * 7) % 30);
      g.fillRect(Math.round(sx), GROUND_Y - h, 6, h);
    }
    g.fillStyle = TRUNK;
    for (let i = 0; i < 100; i++) {
      const wx = i * stride + 20;
      const sx = wx * trunkParallax + offsetX;
      if (sx < -8 || sx > WIDTH + 8) continue;
      const h = 80 + ((i * 7) % 30);
      g.fillRect(Math.round(sx), GROUND_Y - h, 4, h);
    }
  }

  private renderGround(g: CanvasRenderingContext2D): void {
    g.fillStyle = GROUND_EDGE;
    g.fillRect(0, GROUND_Y - 1, WIDTH, 1);
    g.fillStyle = GROUND_TOP;
    g.fillRect(0, GROUND_Y, WIDTH, 6);
    g.fillStyle = GROUND_BOTTOM;
    g.fillRect(0, GROUND_Y + 6, WIDTH, HEIGHT - (GROUND_Y + 6));

    // Carve out pits AND gator ponds. Pit variant determines fill:
    //   - 'pit'      → bottomless black void
    //   - 'tar'      → dark purple sheen with reflective highlight
    //   - 'quicksand'→ muddy ochre band at ground level (not deep)
    for (const p of this.pits) {
      const sx = p.x - this.cameraX;
      if (sx + p.w < 0 || sx > WIDTH) continue;
      const ix = Math.round(sx);
      if (p.kind === 'quicksand') {
        // Quicksand sits AT ground level — band, not a void.
        g.fillStyle = QUICKSAND_DARK;
        g.fillRect(ix, GROUND_Y, p.w, 6);
        g.fillStyle = QUICKSAND;
        g.fillRect(ix, GROUND_Y, p.w, 3);
        // Bubble glyphs — 2 small dots at fixed positions for visual life.
        g.fillStyle = QUICKSAND_DARK;
        g.fillRect(ix + Math.floor(p.w * 0.3), GROUND_Y + 1, 1, 1);
        g.fillRect(ix + Math.floor(p.w * 0.7), GROUND_Y + 2, 1, 1);
      } else if (p.kind === 'tar') {
        // Tar — full pit-depth but with a reflective highlight stripe.
        g.fillStyle = TAR;
        g.fillRect(ix, GROUND_Y - 1, p.w, HEIGHT - (GROUND_Y - 1));
        g.fillStyle = TAR_HIGHLIGHT;
        g.fillRect(ix + 2, GROUND_Y + 1, p.w - 4, 1);
      } else {
        // Plain bottomless pit.
        g.fillStyle = PIT;
        g.fillRect(ix, GROUND_Y - 1, p.w, HEIGHT - (GROUND_Y - 1));
      }
    }
    for (const pond of this.gatorPonds) {
      const sx = pond.x - this.cameraX;
      if (sx + pond.w < 0 || sx > WIDTH) continue;
      g.fillStyle = PIT;
      g.fillRect(Math.round(sx), GROUND_Y - 1, pond.w, HEIGHT - (GROUND_Y - 1));
    }
    // Lip ridges — 1px markers at the front edge of every hazard. Skip
    // quicksand (it's a flat band, no lip).
    g.fillStyle = GROUND_EDGE;
    for (const p of this.pits) {
      if (p.kind === 'quicksand') continue;
      const sx = p.x - this.cameraX;
      if (sx + p.w < 0 || sx > WIDTH) continue;
      g.fillRect(Math.round(sx) - 1, GROUND_Y - 2, 1, 1);
      g.fillRect(Math.round(sx) + p.w, GROUND_Y - 2, 1, 1);
    }
    for (const pond of this.gatorPonds) {
      const sx = pond.x - this.cameraX;
      if (sx + pond.w < 0 || sx > WIDTH) continue;
      g.fillRect(Math.round(sx) - 1, GROUND_Y - 2, 1, 1);
      g.fillRect(Math.round(sx) + pond.w, GROUND_Y - 2, 1, 1);
    }
  }

  private renderCreatures(g: CanvasRenderingContext2D): void {
    for (const c of this.creatures) {
      const b = this.creatureBox(c);
      const sx = b.x - this.cameraX;
      if (sx + b.w < -4 || sx > WIDTH + 4) continue;
      const ix = Math.round(sx);
      const iy = Math.round(b.y);
      switch (c.kind) {
        case 'snake': {
          // Slithering body — alternating-color segments give a wave look.
          g.fillStyle = SNAKE_DARK;
          g.fillRect(ix, iy, b.w, b.h);
          g.fillStyle = SNAKE_BODY;
          for (let i = 0; i < b.w; i += 4) {
            g.fillRect(ix + i, iy + 1, 2, b.h - 2);
          }
          // Head dot — facing direction inferred from sin derivative sign.
          const t = this.worldTicks + c.phase;
          const movingRight =
            Math.cos((t / 60) * (SNAKE_SPEED / SNAKE_RANGE)) > 0;
          g.fillStyle = SNAKE_DARK;
          g.fillRect(
            movingRight ? ix + b.w - 2 : ix,
            iy + 1,
            2,
            b.h - 2
          );
          break;
        }
        case 'scorpion': {
          // Body — dark outline + bright red interior + small tail arc.
          g.fillStyle = SCORPION_DARK;
          g.fillRect(ix, iy, b.w, b.h);
          g.fillStyle = SCORPION_BODY;
          g.fillRect(ix + 1, iy + 1, b.w - 2, b.h - 2);
          // Tail — single px arch above the body, alternates side with phase.
          const t = this.worldTicks + c.phase;
          const tailSide =
            Math.cos((t / 60) * (SCORPION_SPEED / SCORPION_RANGE)) > 0 ? 1 : -1;
          const tailX = ix + (tailSide > 0 ? b.w - 1 : 0);
          g.fillStyle = SCORPION_DARK;
          g.fillRect(tailX, iy - 2, 1, 2);
          break;
        }
        case 'log': {
          // Rolling log — body + dark rings that rotate with x position.
          g.fillStyle = LOG_DARK;
          g.fillRect(ix, iy, b.w, b.h);
          g.fillStyle = LOG_BODY;
          g.fillRect(ix + 1, iy + 1, b.w - 2, b.h - 2);
          // Ring marks — 2 px-wide stripes that shift x with the rolling
          // motion to suggest rotation.
          const phase = Math.floor(b.x) % 4;
          g.fillStyle = LOG_RING;
          for (let stripe = 0; stripe < 3; stripe++) {
            const stripeX = ix + ((stripe * 6 + phase) % b.w);
            if (stripeX < ix || stripeX > ix + b.w - 2) continue;
            g.fillRect(stripeX, iy + 1, 2, b.h - 2);
          }
          break;
        }
      }
    }
  }

  private renderGators(g: CanvasRenderingContext2D): void {
    for (const pond of this.gatorPonds) {
      const sx = pond.x - this.cameraX;
      if (sx + pond.w < -GATOR_W || sx > WIDTH + GATOR_W) continue;
      const slotW = pond.w / 3;
      for (let slot = 0; slot < 3; slot++) {
        const centerX = sx + slot * slotW + slotW / 2;
        const top = GROUND_Y - 4;
        const open = this.gatorMouthOpen(slot);
        // Body — darker back arch behind the lighter belly.
        g.fillStyle = GATOR_BACK;
        g.fillRect(
          Math.round(centerX - GATOR_W / 2),
          top,
          GATOR_W,
          GATOR_H
        );
        g.fillStyle = GATOR_BODY;
        g.fillRect(
          Math.round(centerX - GATOR_W / 2 + 1),
          top + 2,
          GATOR_W - 2,
          GATOR_H - 2
        );
        // Eyes — two yellow dots, only visible when mouth is closed
        // (open mouth covers the whole face).
        if (!open) {
          g.fillStyle = GATOR_EYE;
          g.fillRect(Math.round(centerX - 4), top + 2, 2, 2);
          g.fillRect(Math.round(centerX + 2), top + 2, 2, 2);
        } else {
          // Open mouth — teeth row arcing upward.
          g.fillStyle = GATOR_TEETH;
          for (let tx = 0; tx < GATOR_W - 2; tx += 2) {
            const teethX = Math.round(centerX - GATOR_W / 2 + 1 + tx);
            // Triangular tooth: tall in the middle, short at edges.
            const fromMid = Math.abs(tx - (GATOR_W - 2) / 2);
            const toothH = 6 - Math.floor(fromMid / 2);
            g.fillRect(teethX, top - toothH, 1, toothH);
          }
        }
      }
    }
  }

  private renderVines(g: CanvasRenderingContext2D): void {
    for (let i = 0; i < this.vines.length; i++) {
      const v = this.vines[i]!;
      // Cull off-screen vines.
      const ax = v.anchorX - this.cameraX;
      if (ax < -v.length - 20 || ax > WIDTH + v.length + 20) continue;
      const tip = this.vineTip(v);
      const tx = tip.x - this.cameraX;
      const ty = tip.y;
      // Vine cord — neon cyan line + darker shadow underneath.
      g.strokeStyle = VINE_DARK;
      g.lineWidth = 3;
      g.beginPath();
      g.moveTo(ax, VINE_ANCHOR_Y);
      g.lineTo(tx, ty);
      g.stroke();
      g.strokeStyle = VINE;
      g.lineWidth = 1;
      g.beginPath();
      g.moveTo(ax, VINE_ANCHOR_Y);
      g.lineTo(tx, ty);
      g.stroke();
      // Tip knot — small bright circle at the tip; flashes brighter when
      // the player is in grab range (= the active visual affordance).
      const playerCx = this.playerX + PLAYER_W / 2;
      const playerCy = this.playerY + PLAYER_H / 2;
      const dx = tip.x - playerCx;
      const dy = tip.y - playerCy;
      const grabbable =
        i !== this.grabbedVineIdx &&
        this.grabbedVineIdx < 0 &&
        !this.grounded &&
        dx * dx + dy * dy < VINE_GRAB_R * VINE_GRAB_R * 1.5;
      g.fillStyle = grabbable ? VINE_HIGHLIGHT : VINE;
      g.beginPath();
      g.arc(tx, ty, 3, 0, Math.PI * 2);
      g.fill();
    }
  }

  private renderPlayer(g: CanvasRenderingContext2D): void {
    // Hide / flicker during respawn invuln.
    if (this.phase === 'respawn' && Math.floor(this.invulnTicks / 4) % 2 === 0) {
      return;
    }

    const sx = Math.round(this.playerX - this.cameraX);
    const sy = Math.round(this.playerY);

    g.fillStyle = PLAYER_OUTLINE;
    g.fillRect(sx - 1, sy - 1, PLAYER_W + 2, PLAYER_H + 2);
    g.fillStyle = PLAYER_BODY;
    g.fillRect(sx, sy, PLAYER_W, PLAYER_H);
    g.fillStyle = PLAYER_HEAD;
    g.fillRect(sx + 2, sy + 2, PLAYER_W - 4, 6);
    g.fillStyle = PLAYER_HAT;
    g.fillRect(sx + 1, sy, PLAYER_W - 2, 3);
    g.fillRect(sx + (this.facing === 1 ? 3 : -1), sy + 2, PLAYER_W - 2, 2);

    if (
      this.walkAnimTicks > 0 &&
      this.grounded &&
      Math.floor(this.walkAnimTicks / 8) % 2 === 0
    ) {
      g.fillStyle = PLAYER_OUTLINE;
      g.fillRect(sx + 1, sy + PLAYER_H - 2, 3, 2);
      g.fillRect(sx + PLAYER_W - 4, sy + PLAYER_H - 4, 3, 2);
    }
  }

  private renderPortals(g: CanvasRenderingContext2D): void {
    // Portal = archway carved into the ground at the screen's center-x.
    // Renders as a dark recessed doorway with a cyan glyph above to
    // signal "press DOWN here." Glyph pulses with sun phase so the
    // affordance reads as an active interactable.
    for (const p of this.portals) {
      const sx = p.x - this.cameraX;
      if (sx < -CAVE_PORTAL_W || sx > WIDTH + CAVE_PORTAL_W) continue;
      const ix = Math.round(sx - CAVE_PORTAL_W / 2);
      // Recessed doorway: dark fill into the ground.
      g.fillStyle = CAVE_BG;
      g.fillRect(ix, GROUND_Y, CAVE_PORTAL_W, CAVE_PORTAL_H);
      // Arch outline.
      g.fillStyle = CAVE_WALL;
      g.fillRect(ix - 1, GROUND_Y, 1, CAVE_PORTAL_H);
      g.fillRect(ix + CAVE_PORTAL_W, GROUND_Y, 1, CAVE_PORTAL_H);
      g.fillRect(ix, GROUND_Y, CAVE_PORTAL_W, 1);
      // Affordance arrow + ladder rung hint just above the doorway.
      const pulse = Math.floor(this.worldTicks / 20) % 2 === 0;
      g.fillStyle = pulse ? LADDER_RUNG : LADDER;
      g.fillRect(ix + CAVE_PORTAL_W / 2 - 1, GROUND_Y - 6, 2, 4);
      g.fillRect(ix + CAVE_PORTAL_W / 2 - 3, GROUND_Y - 4, 6, 1);
    }
  }

  private renderCave(g: CanvasRenderingContext2D): void {
    if (this.currentCaveIdx < 0) return;
    const cave = this.caves[this.currentCaveIdx]!;
    const camY = this.caveCameraY;

    // Background — cave gradient (lighter near top entrance, darker at
    // the bottom). Hint of sky bleeds through the top.
    const bg = g.createLinearGradient(0, 0, 0, HEIGHT);
    bg.addColorStop(0, CAVE_WALL);
    bg.addColorStop(1, CAVE_BG);
    g.fillStyle = bg;
    g.fillRect(0, 0, WIDTH, HEIGHT);

    // Wall texture — sparse darker speckles to convey rock. Deterministic
    // pattern, not animated; doesn't consume PRNG (just integer hash).
    g.fillStyle = CAVE_WALL_DARK;
    for (let i = 0; i < 70; i++) {
      const wx = (i * 53 + 11) % WIDTH;
      const wy = (i * 79 + 31) % CAVE_H;
      const sy = wy - camY;
      if (sy < -2 || sy > HEIGHT) continue;
      g.fillRect(wx, Math.round(sy), 2, 2);
    }

    // Ladder — runs the full vertical extent of the cave between the
    // top platform's underside and the bottom platform's top. Two
    // rails + rungs at fixed spacing.
    const ladderTop = cave.topY + CAVE_PLATFORM_H;
    const ladderBot = cave.bottomY;
    {
      const lsy0 = ladderTop - camY;
      const lsy1 = ladderBot - camY;
      g.fillStyle = LADDER;
      g.fillRect(LADDER_X, Math.round(lsy0), 1, Math.round(lsy1 - lsy0));
      g.fillRect(
        LADDER_X + LADDER_W - 1,
        Math.round(lsy0),
        1,
        Math.round(lsy1 - lsy0)
      );
      g.fillStyle = LADDER_RUNG;
      for (let y = ladderTop + 8; y < ladderBot; y += 12) {
        const sy = y - camY;
        if (sy < -2 || sy > HEIGHT) continue;
        g.fillRect(LADDER_X + 1, Math.round(sy), LADDER_W - 2, 1);
      }
    }

    // Platforms.
    for (const plat of cave.platforms) {
      const sy = plat.y - camY;
      if (sy + CAVE_PLATFORM_H < 0 || sy > HEIGHT) continue;
      g.fillStyle = CAVE_WALL;
      g.fillRect(Math.round(plat.x), Math.round(sy), plat.w, CAVE_PLATFORM_H);
      g.fillStyle = CAVE_FLOOR;
      g.fillRect(
        Math.round(plat.x),
        Math.round(sy),
        plat.w,
        2
      );
    }

    // Idol — sitting on the bottom pedestal until picked up. Glows
    // and pulses to draw the eye after the descent. Disappears once
    // collected (idolsCollected has the cave's idx).
    if (!this.idolsCollected.has(this.currentCaveIdx)) {
      const sy = cave.idolY - camY;
      if (sy + IDOL_H >= 0 && sy <= HEIGHT) {
        const ix = Math.round(cave.idolX - IDOL_W / 2);
        const iy = Math.round(sy);
        const glowPulse = Math.floor(this.worldTicks / 8) % 4;
        // Outer glow halo — slightly bigger every 4 ticks.
        g.fillStyle = IDOL_GLOW;
        g.fillRect(ix - 1, iy - 1, IDOL_W + 2, IDOL_H + 2);
        if (glowPulse === 0) {
          g.fillRect(ix - 2, iy + 2, 1, IDOL_H - 4);
          g.fillRect(ix + IDOL_W + 1, iy + 2, 1, IDOL_H - 4);
        }
        // Body — squat idol silhouette.
        g.fillStyle = IDOL_BODY;
        g.fillRect(ix, iy, IDOL_W, IDOL_H);
        // Eye gem — single pink pixel that flickers with the pulse.
        g.fillStyle = glowPulse < 2 ? IDOL_EYE : FLASH_PICKUP;
        g.fillRect(ix + IDOL_W / 2 - 1, iy + 4, 2, 2);
        // Base — darker pixel band at the foot.
        g.fillStyle = CAVE_FLOOR;
        g.fillRect(ix, iy + IDOL_H - 2, IDOL_W, 2);
      }
    }

    // Bats.
    for (const b of cave.bats) {
      const p = this.batPos(b);
      const sy = p.y - camY;
      if (sy < -BAT_H || sy > HEIGHT + BAT_H) continue;
      const ix = Math.round(p.x - BAT_W / 2);
      const iy = Math.round(sy - BAT_H / 2);
      // Wing flap — alternates wide / narrow with worldTicks for life.
      const flap = Math.floor((this.worldTicks + b.phase) / 6) % 2;
      g.fillStyle = BAT_BODY;
      if (flap) {
        g.fillRect(ix, iy + 2, BAT_W, BAT_H - 2);
        g.fillRect(ix - 2, iy + 3, 2, 2);
        g.fillRect(ix + BAT_W, iy + 3, 2, 2);
      } else {
        g.fillRect(ix + 2, iy, BAT_W - 4, BAT_H);
        g.fillRect(ix, iy + 3, 2, 2);
        g.fillRect(ix + BAT_W - 2, iy + 3, 2, 2);
      }
      g.fillStyle = BAT_EYE;
      g.fillRect(ix + 4, iy + 3, 1, 1);
      g.fillRect(ix + BAT_W - 5, iy + 3, 1, 1);
    }

    // Top opening — a light slit above the top platform to suggest the
    // sky / portal you came from.
    {
      const sy = cave.topY - camY;
      if (sy > 0 && sy < HEIGHT) {
        g.fillStyle = SKY_HORIZON;
        g.fillRect(
          LADDER_X - 8,
          Math.max(0, Math.round(sy - 6)),
          LADDER_W + 16,
          Math.min(6, Math.round(sy))
        );
      }
    }

    // Player — in cave coords, camera-offset vertically.
    const psx = Math.round(this.playerX);
    const psy = Math.round(this.playerY - camY);
    g.fillStyle = PLAYER_OUTLINE;
    g.fillRect(psx - 1, psy - 1, PLAYER_W + 2, PLAYER_H + 2);
    g.fillStyle = PLAYER_BODY;
    g.fillRect(psx, psy, PLAYER_W, PLAYER_H);
    g.fillStyle = PLAYER_HEAD;
    g.fillRect(psx + 2, psy + 2, PLAYER_W - 4, 6);
    g.fillStyle = PLAYER_HAT;
    g.fillRect(psx + 1, psy, PLAYER_W - 2, 3);
  }

  private renderHud(g: CanvasRenderingContext2D): void {
    g.font = '10px ui-monospace, monospace';
    g.textBaseline = 'top';

    g.textAlign = 'left';
    g.fillStyle = HUD_LABEL;
    g.fillText('SCORE', 8, 6);
    g.fillStyle = HUD_SCORE;
    g.fillText(
      String(this.scorePoints + this.idolBonus).padStart(6, '0'),
      8,
      18
    );

    g.fillStyle = HUD_LABEL;
    g.fillText('LIVES', 8, 32);
    g.fillStyle = HUD_LIVES;
    for (let i = 0; i < this.lives; i++) {
      g.fillRect(8 + i * 10, 46, 6, 8);
    }

    // Idol counter — small jade square + collected/total readout.
    if (this.caves.length > 0) {
      g.fillStyle = IDOL_BODY;
      g.fillRect(60, 32, 6, 8);
      g.fillStyle = HUD_LABEL;
      g.fillText(
        `${this.idolsCollected.size}/${this.caves.length}`,
        72,
        32
      );
    }

    // TIME — sun-progress readout. Color ramps as sundown approaches.
    // While in cave, label flips to "TIME (PAUSED)" and color stays
    // muted (no urgency — cave time is free, exit cost is fixed).
    g.textAlign = 'right';
    g.fillStyle = HUD_LABEL;
    g.fillText(this.phase === 'cave' ? 'TIME PAUSED' : 'TIME', WIDTH - 8, 6);
    const remaining = Math.max(0, SUN_TOTAL_TICKS - this.sunTicks);
    const remainingSec = Math.ceil(remaining / 60);
    const sunFrac = this.sunTicks / SUN_TOTAL_TICKS;
    const timeColor =
      this.phase === 'cave'
        ? HUD_LABEL
        : sunFrac >= 0.85
          ? HUD_TIME_CRIT
          : sunFrac >= 0.6
            ? HUD_TIME_WARN
            : HUD_TIME_OK;
    g.fillStyle = timeColor;
    g.fillText(`${remainingSec}s`, WIDTH - 8, 18);

    // Cave exit hint — small prompt at bottom when the player is on the
    // top platform inside the cave, telling them UP exits.
    if (this.phase === 'cave' && this.currentCaveIdx >= 0) {
      const cave = this.caves[this.currentCaveIdx]!;
      const onTopPlatform =
        this.grounded &&
        Math.abs(this.playerY + PLAYER_H - cave.topY) < 2;
      if (onTopPlatform) {
        g.textAlign = 'center';
        g.fillStyle = LADDER_RUNG;
        g.fillText('PRESS UP TO EXIT', WIDTH / 2, HEIGHT - 16);
      }
    }
  }

  private renderGameOver(g: CanvasRenderingContext2D): void {
    g.fillStyle = 'rgba(0,0,0,0.72)';
    g.fillRect(0, 0, WIDTH, HEIGHT);

    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = '24px ui-monospace, monospace';
    // WIN > SUNDOWN > GAME OVER. Win-win color = idol jade-cyan;
    // sundown color = sun-pink crit; death color = white-hot.
    let headline: string;
    let headlineColor: string;
    if (this.finished) {
      headline = 'TREASURE!';
      headlineColor = IDOL_BODY;
    } else if (this.sunTicks >= SUN_TOTAL_TICKS) {
      headline = 'SUNDOWN';
      headlineColor = HUD_TIME_CRIT;
    } else {
      headline = 'GAME OVER';
      headlineColor = FLASH_HIT;
    }
    g.fillStyle = headlineColor;
    g.fillText(headline, WIDTH / 2, HEIGHT / 2 - 32);

    g.font = '12px ui-monospace, monospace';
    g.fillStyle = HUD_SCORE;
    g.fillText(`SCORE ${this.finalScore}`, WIDTH / 2, HEIGHT / 2);

    // Breakdown line — small, beneath the final score, so the player can
    // see WHERE the points came from. Helps the meta-game ("next run I
    // grab one more idol").
    g.font = '9px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    const dist = this.scorePoints;
    const idols = this.idolBonus;
    const finish = this.finished ? FINISHING_BONUS : 0;
    const breakdown =
      finish > 0
        ? `DIST ${dist}  +IDOLS ${idols}  +FINISH ${finish}`
        : `DIST ${dist}  +IDOLS ${idols}`;
    g.fillText(breakdown, WIDTH / 2, HEIGHT / 2 + 18);

    g.font = '10px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    g.fillText('PRESS Z TO RETRY', WIDTH / 2, HEIGHT / 2 + 42);
  }
}
